/**
 * Le visuel d'une carte de formation, CALCULÉ (#74, option A).
 *
 * Les maquettes de référence posent une illustration par cours. Nous n'avons pas
 * cette donnée — ni `Session` ni `ScormModule` ne portent de champ visuel — et
 * en ajouter un ferait porter au client la production d'une image par module
 * déjà en base, avec une grille à moitié vide en attendant.
 *
 * D'où un aplat dérivé de la marque. Deux exigences se croisent ici :
 *
 * 1. **Rien en dur.** Depuis #31 l'apprenant voit les couleurs de SON organisme.
 *    Un dégradé écrit en hexadécimal défait ce qui vient d'être livré, et ne
 *    tiendrait de toute façon pas sur les deux thèmes. Tout passe donc par
 *    `color-mix()` sur les jetons — `contient-des-jetons-uniquement` est épinglé
 *    par un test, parce que c'est le genre de règle qu'une retouche pressée
 *    oublie.
 *
 * 2. **Stable par module.** Un module doit avoir le même visuel d'une visite à
 *    l'autre : c'est ce qui en fait un repère plutôt qu'une décoration. La
 *    variante est donc tirée de son id, pas d'un aléa ni de sa position — la
 *    position change quand le formateur réordonne le parcours.
 */

/**
 * Nombre de variantes. Premier avec le nombre de motifs, pour que les deux ne se
 * synchronisent pas : sinon la grille paraîtrait se répéter à intervalle fixe.
 */
const VARIANTS = 7

/** Motifs géométriques disponibles, cf. `ModuleArtwork`. */
export const PATTERNS = 4

/**
 * FNV-1a 32 bits.
 *
 * Choisie pour ce qu'elle n'est pas : une dépendance. On répartit des uuid sur
 * sept cases, pas des secrets — ce qu'il faut, c'est le même résultat partout et
 * une distribution qui ne colle pas deux uuid voisins sur la même variante.
 */
export function hashId(id: string): number {
  let hash = 0x811C9DC5
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i)
    // Multiplication par 16777619 en décomposition, pour rester en 32 bits non
    // signés : `hash * 16777619` dépasserait la précision d'un Number.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0
  }
  return hash
}

export type ModuleVisual = {
  /** Valeur CSS de `background`, en jetons uniquement. */
  background: string
  /** Index du motif à superposer, dans [0, PATTERNS[. */
  pattern: number
}

/**
 * Les sept variantes, en parts de mélange. `primary` et `accent` sont les jetons
 * de marque, `card` le fond de la carte.
 *
 * Le second arrêt tire toujours vers `card` : sans lui, une organisation qui n'a
 * rien configuré — le jeton `--color-primary` vaut alors `#000000` en thème
 * clair — recevrait un aplat noir sur chaque carte. Mélanger vers le fond donne
 * un gris chaud, discret, qui reste une surface et non une tache.
 */
const MIXES: { angle: number, from: number, to: number }[] = [
  { angle: 135, from: 62, to: 30 },
  { angle: 160, from: 48, to: 22 },
  { angle: 115, from: 72, to: 36 },
  { angle: 200, from: 55, to: 26 },
  { angle: 145, from: 40, to: 18 },
  { angle: 175, from: 66, to: 32 },
  { angle: 125, from: 52, to: 40 },
]

/**
 * Visuel d'un module, dérivé de son id.
 *
 * `in oklab` et non l'espace sRGB par défaut : un mélange sRGB entre deux
 * teintes éloignées passe par un gris terne au milieu du dégradé. OKLab suit la
 * perception, et c'est tout l'intérêt ici — le dégradé doit rester vivant quelle
 * que soit la couleur de marque, y compris entre un bleu nuit et un beige.
 */
export function moduleVisual(id: string): ModuleVisual {
  const hash = hashId(id)
  const mix = MIXES[hash % VARIANTS]!
  return {
    background: `linear-gradient(${mix.angle}deg, `
      + `color-mix(in oklab, var(--color-primary) ${mix.from}%, var(--color-accent)), `
      + `color-mix(in oklab, var(--color-accent) ${mix.to}%, var(--color-card)))`,
    // Décalé par un second prélèvement dans le hash : prendre `hash % PATTERNS`
    // corrélerait motif et dégradé, et la grille paraîtrait se répéter.
    pattern: (hash >>> 8) % PATTERNS,
  }
}
